import * as Sentry from '@sentry/react';
import { fetchAuthSession } from 'aws-amplify/auth';

/** Options accepted by every token getter, including injected test doubles. */
export interface AuthTokenOptions {
  /**
   * Ask Amplify to refresh the tokens even if the cached ones are not yet
   * expired. Used to recover a long-lived session in place after a 401 (see
   * `fetchWithAuthRecovery`) instead of freezing until a manual page reload.
   */
  forceRefresh?: boolean;
}

/**
 * Acquires the current auth token, or `null` when there is no usable session.
 *
 * Named as a type so the three injected `getToken` seams (the WebSocket handshake
 * and both reconnect catch-ups) share one contract and did not each need editing
 * when `forceRefresh` was added.
 */
export type AuthTokenGetter = (
  options?: AuthTokenOptions,
) => Promise<string | null>;

/**
 * Whether a token was successfully acquired at any point on THIS page load.
 *
 * This is the only thing that distinguishes "not logged in yet" from "logged out
 * under us", because Amplify reports both the same way: `fetchAuthSession`
 * resolves with no tokens for an expired refresh token just as it does before
 * sign-in. `authTokenMissingError` reads it to pick between an ignorable
 * pre-login state and a session loss that must trigger re-authentication.
 */
let acquiredTokenThisLoad = false;

export const hasAcquiredTokenThisLoad = (): boolean => acquiredTokenThisLoad;

/** Test-only. Resets the page-load token-acquisition flag. */
export const __resetTokenAcquired = (): void => {
  acquiredTokenThisLoad = false;
};

// A Cognito/IdP outage makes every call throw for its whole duration, and this
// runs on every request, both catch-ups and every socket reconnect. Reporting
// per call would be thousands of events per tab per hour, so it is reported once
// per page load like the rest of the auth telemetry.
let reportedSessionFetchFailure = false;

export const getAuthToken: AuthTokenGetter = async options => {
  try {
    const authSession = await fetchAuthSession(
      options?.forceRefresh ? { forceRefresh: true } : undefined,
    );
    const accessToken = authSession.tokens?.accessToken.toString();
    const idToken = authSession.tokens?.idToken?.toString();

    if (accessToken && idToken) {
      acquiredTokenThisLoad = true;
      return `${accessToken}:${idToken}`;
    }
    return null;
  } catch (error) {
    // A THROW here is a transient session-retrieval failure — a Cognito/IdP
    // outage, a rate limit, an Amplify misconfig. Note it is NOT how an expired
    // refresh token surfaces: Amplify resolves with no tokens for that (it only
    // rethrows non-`NotAuthorizedException` errors), which is why the falsy-token
    // path, not this one, owns detecting a lost session. Capture it so a recovery
    // failure is debuggable and an outage is not mislabeled as a mass logout. We
    // still return null: callers treat that as "not authenticated".
    if (!reportedSessionFetchFailure) {
      reportedSessionFetchFailure = true;
      Sentry.captureException(error, {
        tags: {
          category: 'auth',
          ['auth_error']: 'session_fetch_failed',
        },
      });
    }
    return null;
  }
};
